const VEG_COA = [
  { param: "Appearance", value: "Characteristic; free-flowing powder, no visible mould/infestation" },
  { param: "Colour", value: "Characteristic of source vegetable; no scorching/discolouration" },
  { param: "Odour", value: "Characteristic; free from musty/foreign odour" },
  { param: "Moisture", value: "NMT 5.0 %" },
  { param: "Acid Insoluble Ash", value: "NMT 0.5 %" },
  { param: "Peroxidase", value: "Negative" },
  { param: "Foreign Matter", value: "Absent" },
  { param: "Total Plate Count", value: "NMT 3.0 × 10⁵ cfu/g" },
  { param: "Yeast & Mould", value: "NMT 1.0 × 10³ cfu/g" },
  { param: "E. coli", value: "Absent / g" },
  { param: "Salmonella", value: "Absent in 25 g" },
];

const CARAMEL_CLASSES = {
  1: { ins: "150a", solids: "62–77 % (solids basis)", colour: "0.01–0.12", nh3: "NMT 0.01 %", mei: "Not specified for Class I" },
  2: { ins: "150b", solids: "65–72 % (solids basis)", colour: "0.06–0.10", nh3: "NMT 0.01 %", mei: "Not specified for Class II in cited FSSAI table" },
  3: { ins: "150c", solids: "53–83 % (solids basis)", colour: "0.08–0.36", nh3: "NMT 0.4 %", mei: "NMT 300 mg/kg and NMT 200 mg/kg on equivalent colour basis" },
  4: { ins: "150d", solids: "40–75 % (solids basis)", colour: "0.10–0.60", nh3: "NMT 0.5 %", mei: "NMT 1000 mg/kg and NMT 250 mg/kg on equivalent colour basis" },
};

const caramelCoa = (cls) => {
  const c = CARAMEL_CLASSES[cls];
  return [
    { param: "Description", value: "Dark brown to black liquid/solid; burnt-sugar odour", ref: "FSSAI/JECFA" },
    { param: "Solid content", value: c.solids, ref: "FSSAI" },
    { param: "Colour intensity", value: c.colour, ref: "FSSAI" },
    { param: "Ammoniacal nitrogen", value: c.nh3, ref: "FSSAI" },
    { param: "4-MEI", value: c.mei, ref: "FSSAI" },
    { param: "Lead", value: "NMT 5 mg/kg", ref: "FSSAI" },
    { param: "Arsenic", value: "NMT 3 mg/kg", ref: "FSSAI" },
    { param: "Mercury", value: "NMT 1 mg/kg*", ref: "Internal QC limit" },
    { param: "Cadmium", value: "NMT 1 mg/kg*", ref: "Internal QC limit" },
    { param: "Microbiology", value: "Food-grade compliant; product-specific limits validated by MSD Foods QC", ref: "MSD Foods QC" },
  ];
};

const FIRST_AID = [
  { route: "Inhalation", action: "Move to fresh air." },
  { route: "Eye contact", action: "Rinse with water for several minutes." },
  { route: "Skin contact", action: "Wash with soap and water." },
  { route: "Ingestion", action: "Rinse mouth; seek medical advice if symptoms persist or an unusual reaction occurs." },
];

const vegMsds = {
  hazard: "Not expected to be classified as hazardous under GHS (food-grade composition). Dust from powders may cause temporary mechanical eye/respiratory irritation.",
  composition: "Dehydrated vegetable material — single-ingredient food-grade powder. Exact formulation, carriers and processing aids per approved formulation.",
  firstAid: FIRST_AID,
  handling: "Keep container closed. Store cool, dry, clean and well ventilated; protect from moisture, pests, contamination, heat and strong odours. Follow product label shelf life.",
  properties: [
    { param: "Appearance", value: "Characteristic free-flowing powder" },
    { param: "Odour", value: "Characteristic of source vegetable" },
    { param: "Solubility", value: "Product-specific" },
    { param: "pH", value: "Product-specific" },
  ],
  stability: "Stable under recommended storage. Avoid excessive heat, moisture, contamination and incompatible strong oxidising agents.",
  disposal: "Dispose according to local regulations. Handle contaminated product and packaging as required by local waste rules.",
  transport: "Not expected to be regulated as dangerous goods. Protect from moisture, contamination and physical damage.",
};

const caramelMsds = (cls) => ({
  hazard: "Not expected to be classified as hazardous under GHS (food-grade composition). Dust from powders may cause temporary mechanical eye/respiratory irritation.",
  composition: `Caramel Colour Class ${["I", "II", "III", "IV"][cls - 1]} (INS ${CARAMEL_CLASSES[cls].ins}). Exact formulation, carriers and processing aids per approved formulation.`,
  firstAid: FIRST_AID,
  handling: "Keep container closed. Store cool, dry, clean and well ventilated; protect from moisture, pests, contamination, heat and strong odours. Follow product label shelf life.",
  properties: [
    { param: "Appearance", value: "Dark brown to black liquid or solid" },
    { param: "Odour", value: "Burnt-sugar odour" },
    { param: "Solubility", value: "Product-specific (water)" },
    { param: "pH", value: "Product-specific" },
  ],
  stability: "Stable under recommended storage. Avoid excessive heat, moisture, contamination and incompatible strong oxidising agents.",
  disposal: "Dispose according to local regulations. Handle contaminated product and packaging as required by local waste rules.",
  transport: "Not expected to be regulated as dangerous goods. Protect from moisture, contamination and physical damage.",
});

const veg = (sdsNo, name, description) => ({
  sdsNo: `MSD-SDS-${String(sdsNo).padStart(3, "0")}`,
  name,
  category: "flavours",
  description,
  coa: VEG_COA,
  msds: vegMsds,
});

const caramel = (sdsNo, cls, form) => ({
  sdsNo: `MSD-SDS-${String(sdsNo).padStart(3, "0")}`,
  name: `Caramel Colour Type ${cls} — ${form}`,
  category: "caramel",
  description: `Caramel Colour Class ${["I", "II", "III", "IV"][cls - 1]} (INS ${CARAMEL_CLASSES[cls].ins}) – ${form.toLowerCase()}`,
  coa: caramelCoa(cls),
  msds: caramelMsds(cls),
});

export const SPEC_PRODUCTS = [
  veg(1, "Beetroot Powder", "Dehydrated beetroot powder"),
  veg(2, "Bitter Gourd Powder", "Dehydrated bitter gourd powder"),
  veg(3, "Ginger Powder", "Dehydrated ginger powder"),
  veg(4, "Carrot Powder", "Dehydrated carrot powder"),
  veg(5, "Green Chilly Powder", "Dehydrated green chilli powder"),
  veg(6, "Cucumber Powder", "Dehydrated cucumber powder"),
  veg(7, "Spinach Powder", "Dehydrated spinach powder"),
  veg(8, "Onion Powder — Type A", "Dehydrated onion powder – Type A"),
  veg(9, "Onion Powder — Type B", "Dehydrated onion powder – Type B"),
  veg(10, "Garlic Powder — Type A", "Dehydrated garlic powder – Type A"),
  veg(11, "Garlic Powder — Type B", "Dehydrated garlic powder – Type B"),
  caramel(12, 1, "Liquid"),
  caramel(13, 1, "Powder"),
  caramel(14, 2, "Liquid"),
  caramel(15, 2, "Powder"),
  caramel(16, 3, "Liquid"),
  caramel(17, 3, "Powder"),
  caramel(18, 4, "Liquid"),
  caramel(19, 4, "Powder"),
];

export const SPEC_VERSION = { version: "01", issueDate: "19-Aug-2026" };
